import { Section } from "../../../../packages/editing/Section"
import {
    cancelSubscription,
    CurrencyAmount,
    GetPhoneOrderDto,
    getPhoneOrders,
    postOrderTradeIn,
    postRenewal,
    useCancellationTerms,
    useGeneralPhoneOrderTermsAndConditions,
    useInsuranceOptions,
    usePhoneOrder,
    useRenewalOptions,
    useTradeInForm,
} from "../client"
import { Heading } from "../ui/components/typography/Heading"
import { useLocalize } from "../../../../packages/localization/client-side/useLocalize"
import advancedFormat from "dayjs/plugin/advancedFormat"
import { Button } from "../ui/components/buttons/Button"
import { useSearchParams } from "react-router-dom"
import dayjs from "dayjs"
import { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { Flex } from "../ui/components/base/Flex"
import { responsiveCss, scaleValue } from "../ui/helpers/css"
import { css } from "@emotion/react"
import { Text } from "../ui/components/typography/Text"
import { Modal } from "../ui/components/modal/Modal"
import { MarkdownView } from "../ui/components/base/MarkdownView"
import { Icon, IconName } from "../ui/components/visual/Icon"
import { Divider } from "../ui/components/other/Divider"
import { CheckoutStepSummaryBlock } from "../ui/components/blocks/CheckoutStepSummaryBlock"
import { PaymentSummary } from "../ui/components/forms/PaymentSummary"
import { Markdown, Uuid } from "../../../../reactor"
import {
    localizedCurrencyCodes,
    monthsLong,
    monthsShort,
    useFormatAmount,
} from "../ui/constants/text"
import { Localized } from "../../../../packages/localization/Localized"
import { MustacheString } from "../../../../packages/editing/Mustache"
import { PhoneOrderStatus } from "../../model/PhoneOrder"
import { RadioGroup } from "../ui/components/forms/RadioGroup"
import { Checkbox } from "../ui/components/controllers/Checkbox"
import { TradeInModal } from "./checkout/TradeInStep"
import { useSessionState } from "../../../../reactor/Web"

type SubscriptionDetailsProps = {
    /**
     * @default '{"no": "Detaljer", "en": "Details"}'
     */
    heading: Localized<string>

    /**
     * @default  '{"no": "Dette abonnementet er kansellert.", "en": "This subscription has been cancelled."}'
     */
    subscriptionIsCancelled: Localized<Markdown>

    status: {
        /**
         * @default '{"no": "Status"}'
         */
        heading: Localized<string>

        /**
         * @default more
         */
        icon: IconName

        /**
         * Label for any of the statuses an order can have. Any derived statuses like
         * cancellation requested is defined as separate fields.
         *
         * @default '{"new": {"no": "Ordre lagt"}, "shipped": {"no": "Mobilen er på vei til deg"}}'
         */
        statusLabels: Record<PhoneOrderStatus, Localized<string> | undefined>

        /**
         * Status label if the order is requested cancelled.
         * @default '{ "no": "Kansellering mottatt" }'
         */
        cancellationRequestedStatusLabel?: Localized<string>
    }

    payment: {
        /**
         * @default '{"no": "Månedlig", "en": "Monthly"}'
         */
        monthlyLabel: Localized<string>

        /**
         * @default '{"no": "Pris for telefon."}'
         */
        phonePriceText: Localized<string>

        /**
         * @default '{"no": "Pris for telefon etter innbytterabatt."}'
         */
        phonePriceWithTradeInText: Localized<string>
    }

    subscriptionPeriod: {
        /**
         * Available variables are `{{nextChargeDate}}.`
         * @default '{"no": "Neste forfall", "en": "Next charge date"}'
         */
        heading: Localized<string>

        /**
         * The big text in this component. Available variables are `{{amount}}`.
         * @default '{"no": "Beløp: {{amount}}"}'
         */
        subtitle?: Localized<string>

        /**
         * Discount text. Available variables are `{{discountName}}`, `{{discountAmount}}`, `{{remainingMonths}}`, `{{totalMonths}}`.
         * @default '{"no": "{{discountName}}: {{discountAmount}} i rabatt ({{remainingMonths}} av {{totalMonths}} måneder igjen)."}'
         */
        discountText: Localized<string>

        /**
         * @default '{"no": "{{monthsLeft}} av {{rentalPeriodMonths}} måneder gjenstår av leieperioden."}'
         */
        text: Localized<string>

        /**
         * Label to show in the top right corner of this component.
         * @default '{"no": "Faktura"}'
         */
        label?: Localized<string>
    }

    insurance: {
        /**
         * @default '{"no": "Trygghetspakker"}'
         */
        heading: Localized<string>

        /**
         * @default '{"no": "Vis betingelser", "en": "Show terms and conditions"}'
         */
        showTermsLink: Localized<string>

        /**
         * Title for modal showing terms and conditions. Available variable is `{{insuranceName}}`
         * which is the display name for the insurance.
         * @default '{"no": "Betingelser for {{insuranceName}}", "en": "Terms and conditions for {{insuranceName}}"}'
         */
        termsModalTitle: Localized<string>

        /**
         * Label to show in the top right corner of this component.
         * @default '{"no": "Månedlig"}'
         */
        label?: Localized<string>
    }

    receipts: {
        /**
         * @default '{"no": "Kvitteringer", "en": "Receipts"}'
         */
        heading: Localized<string>

        /**
         * @default '{"no": "Last ned", "en": "Download"}'
         */
        downloadLinkText: Localized<string>

        /**
         * See https://day.js.org/docs/en/display/format for more details.
         *
         * @default '{"no": "DD.MM.YYYY"}'
         */
        dateFormat: Localized<string>

        /**
         * @default '{"no": "Ingen kvitteringer vil være tilgjengelig før telefonen er sendt og kortet er belastet."}'
         */
        noReceiptsMessage: Localized<string>
    }
}

type SubscriptionCancellationProps = {
    /**
     * @default '{"no": "Kanseller abonnement"}'
     */
    title: Localized<string>

    /**
     * Short text for cancellation prompt. Available variables are `{{monthsRemaining}}` and `{{cancellationFee}}`.
     * @default '{"no": "Er du sikker på at du vil kansellere abonnementet ditt? Du har igjen {{monthsRemaining}} måneder av abonnementet, og du vil måtte betale et bruddgebyr på {{cancellationFee}}."}'
     */
    cancellationPrompt: Localized<string>

    /**
     * @default '{"no": "Bekreft kansellering"}'
     */
    confirmButtonText: Localized<string>

    /**
     * @default '{"no": "Nei, jeg ønsker å videreføre abonnementet"}'
     */
    cancelButtonText: Localized<string>

    /**
     * @default '{"no": "Kanselleringen av abonnementet er sendt til oss. Vi vil behandle den så snart som mulig og komme tilbake til deg."}'
     */
    cancellationSuccessText: Localized<Markdown>

    /**
     * @default '{"no": "Det skjedde en feil ved kanselleringen av ditt abonnement. Prøv igjen, eller ta kontakt med kundeservice så skal vi hjelpe deg."}'
     */
    cancellationErrorText: Localized<Markdown>

    /**
     * @default '{"no": "Lukk", "en": "Close"}'
     */
    closeButtonText: Localized<string>
}

type SubscriptionRenewalProps = {
    /**
     * @default '{"no": "Forny abonnement"}'
     */
    title?: Localized<string>

    /**
     * Text describing the renewal concept.
     *
     * @default '{"no": "Ved å fornye abonnementet kan du binde deg til en lengre leieperiode enn du har i dag, mot en lavere månedspris. Se nedenfor for dine alternativer."}'
     */
    text?: Localized<Markdown>

    /**
     * Renewal options label.
     * @default '{"no": "Din nye leieperiode"}'
     */
    renewalOptionsLabel?: Localized<string>

    /**
     * Renew insurance checkbox label.
     * @default '{"no": "Videreføring av {{insuranceName}} (+{{monthlyPrice}}/{{monthsShort}})"}'
     */
    renewInsuranceLabel?: Localized<string>

    /**
     * @default '{"en": "Based on a {{rentalPeriod}}-month subscription", "no": "Basert på en {{rentalPeriod}}-måneders leieperiode"}'
     */
    totalPricePostFix?: Localized<string>

    /**
     * @default '{"no": "Det skjedde en feil ved fornyelsen av ditt abonnement. Prøv igjen, eller ta kontakt med kundeservice så skal vi hjelpe deg."}'
     */
    renewalErrorText?: Localized<Markdown>

    /**
     * @default '{"no": "Totalpris etter {{rentalPeriod}} {{monthsLong}} er {{totalPrice}}"}'
     */
    rentalPeriodTotalPrice?: Localized<string>

    /**
     * @default '{"no": "Forny abonnementet"}'
     */
    confirmButtonText?: Localized<string>

    /**
     * Label for terms and conditions checkbox. Should include a link to the terms and conditions.
     */
    termsAndConditionsLabel?: Localized<Markdown>
}

type SubscriptionTradeInProps = {
    modal: {
        questions: {
            /**
             * @default '{"en": "Trade in your old device"}'
             */
            heading: Localized<string>

            /**
             * @default '{"en": "Continue", "no": "Fortsett"}'
             */
            continueButtonText: Localized<string>
        }

        summary: {
            /**
             * @default '{"en": "Summary", "no": "Oppsummering"}'
             */
            heading: Localized<string>

            /**
             * @default '{"en": "After trade-in", "no": "Etter innbytte"}'
             */
            priceSummaryLabel: Localized<string>

            /**
             * @default '{"en": "Your estimated phone value is {{itemValue}} {{currency}}. This means you will get {{discountAmount}} {{currency}}/mo off your contract for the next {{discountMonths}} months after trading in your device."}'
             */
            priceSummaryText: Localized<string>

            /**
             * @default '{"en": "Trade-in summary", "no": "Innbyttesammendrag"}'
             */
            answersSummaryHeading: Localized<string>

            /**
             * @default '{"en": "Continue", "no": "Fortsett"}'
             */
            continueButtonText: Localized<string>
        }

        confirmation: {
            /**
             * @default '{"en": "Next steps", "no": "Hva skjer videre?"}'
             */
            nextStepsHeading: Localized<string>

            /**
             * @expand
             * @default '[{"id": "%UUID%", "icon": "at", "text": {"en": "You will receive a confirmation email with the free shipping label."}}, {"id": "%UUID%", "icon": "delivery", "text": {"en": "Send us the mobile phone at no cost to you."}}]'
             */
            nextSteps: {
                readonly id: Uuid
                icon: IconName
                /**
                 * @title
                 */
                text: Localized<string>
            }[]

            /**
             * @expand
             * @default '[{"id": "%UUID%", "text": {"en": "I have removed the device from the \"Find My iPhone\" feature. [How to do it](https://www.redoit.no)"}}, {"id": "%UUID%", "text": {"en": "I certify I haven’t lied about the condition of my phone and all the information provided is accurate."}}]'
             */
            acknowledgements: {
                readonly id: Uuid
                /**
                 * @title
                 */
                text: Localized<Markdown>
            }[]

            /**
             * @default '{"en": "Complete and trade-in my device"}'
             */
            continueButtonText: Localized<string>
        }

        closePrompt?: {
            /**
             * @default '{"en": "Are you sure you want to cancel trade-in?"}'
             */
            heading: Localized<string>

            /**
             * @default '{"en": "By trading in you’ll get a monthly discount and contribute to a more sustainable future by supporting the circular economy and reducing environmental impact."}'
             */
            text: Localized<string>

            /**
             * @default '{"en": "No, continue trade-in"}'
             */
            cancelButtonText: Localized<string>

            /**
             * @default '{"en": "Yes, cancel trade-in"}'
             */
            confirmButtonText: Localized<string>
        }
    }
}

Section(SubscriptionOverview)
function SubscriptionOverview(section: {
    menu: {
        /**
         * @default '{"no": "Forny abonnementet", "en": "Renew subscription"}'
         */
        renewSubscription: Localized<string>

        /**
         * @default '{"no": "Kanseller abonnementet", "en": "Cancel subscription"}'
         */
        cancelSubscription: Localized<string>

        /**
         * Text for button that starts the trade-in flow.
         * @default '{"no": "Nytt innbytte"}'
         */
        tradeIn: Localized<string>
    }

    /**
     * @default '{"no": "Ordrenummer", "en": "Order number"}'
     */
    orderNumberLabel: Localized<string>

    /**
     * Title for the modal showing model details if chevron down button is clicked.
     * @default '{"no": "Produktdetaljer", "en": "Product details"}'
     */
    deviceDetailsModalTitle: Localized<string>
    details: SubscriptionDetailsProps
    cancelSubscription: SubscriptionCancellationProps
    renewSubscription: SubscriptionRenewalProps
    tradeIn: SubscriptionTradeInProps
}) {
    const [params, setParams] = useSearchParams()
    const orderId = params.get("id")
    const order = usePhoneOrder((orderId as any) ?? null)
    const localize = useLocalize()
    const [renewalModalIsOpen, setRenewalModalIsOpen] = useState(false)
    const tradeInForm = useTradeInForm("Phones")
    const [tradeInId, setTradeInId] = useSessionState<string | undefined>(
        "tradein-modal-id",
        undefined
    )
    const [tradeInModalIsOpen, setTradeInModalIsOpen] = useSessionState("tradein-modal-open", false)
    const renewalOptions = useRenewalOptions(
        order.data?.status === "delivered"
            ? (orderId as any as Uuid<"PhoneOrder"> | undefined) ?? null
            : null
    )
    const renewalAvailable = !!renewalOptions.data?.options
    const [cancellationModalIsOpen, setCancellationModalIsOpen] = useState(false)

    // If no subscription id is provided, redirect to the most recently placed order. Reason for
    // this being implemented is Redoit want to be able to include a (standard) QR-code in the
    // box when shipping the phones, encouraging making a trade-in if they have not already, that
    // should lead to the subscription page.
    useEffect(() => {
        async function redirectToSubscription() {
            const orders = await getPhoneOrders()
            const activeOrders = [...orders.orders].sort((a, b) => (a.placed < b.placed ? 1 : -1))

            if (!activeOrders[0]) return
            const sp = Object.fromEntries(params)

            setParams(
                {
                    ...sp,
                    id: activeOrders[0].id.valueOf(),
                },
                { replace: true }
            )
        }

        if (!orderId) {
            void redirectToSubscription()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (!order.data) return <></>
    const { model } = order.data

    return (
        <>
            <Flex
                justifyContent="center"
                css={css(
                    { paddingTop: 32, paddingBottom: 32 },
                    responsiveCss("min", "md", { paddingTop: 64, paddingBottom: 64 })
                )}
            >
                <div
                    style={{ maxWidth: 1020, width: "100%" }}
                    css={css(
                        {
                            display: "grid",
                            gridTemplateAreas: '"text details" "menu details"',
                            gridTemplateColumns: "56fr 70fr",
                            gridTemplateRows: "minmax(0, auto)",
                            columnGap: scaleValue(140),
                        },
                        responsiveCss("max", "sm", {
                            gridTemplateAreas: '"text" "details" "menu"',
                            gridTemplateRows: "auto",
                            gridTemplateColumns: "1fr",
                            rowGap: 32,
                        })
                    )}
                >
                    <div
                        css={responsiveCss("min", "md", {
                            marginBottom: 16,
                        })}
                    >
                        <Flex alignItems="center">
                            <Heading level={1} truncate>
                                {localize(model.name)}
                            </Heading>
                        </Flex>
                        {model.features && model.features.length > 0 && (
                            <Flex
                                gap={8}
                                alignItems="center"
                                style={{
                                    overflow: "hidden",
                                }}
                            >
                                <Text
                                    variant="body"
                                    size="xl"
                                    color="gray300"
                                    css={css({
                                        display: "inline-block",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        flex: "1 1 auto",
                                    })}
                                >
                                    {localize(section.orderNumberLabel)}: {order.data.orderNumber}
                                </Text>
                            </Flex>
                        )}
                    </div>
                    <Flex
                        gap={28}
                        direction="column"
                        margin={{ top: scaleValue(32) }}
                        style={{ gridArea: "menu", alignSelf: "start" }}
                    >
                        <Flex
                            style={{ cursor: !order.data.tradeIn ? "pointer" : "inherit" }}
                            justifyContent="space-between"
                            color={order.data.tradeIn ? "gray300" : undefined}
                            alignItems="center"
                            onClick={() => {
                                if (order.data?.tradeIn) {
                                    return
                                }
                                setTradeInId(Uuid().valueOf())
                                setTradeInModalIsOpen(true)
                            }}
                        >
                            <Text variant="heading" level={3}>
                                {localize(section.menu.tradeIn)}
                            </Text>
                            <Icon icon="arrowRight" />
                        </Flex>
                        <Divider horizontal spacing={0} />
                        <Flex
                            style={{ cursor: renewalAvailable ? "pointer" : "inherit" }}
                            justifyContent="space-between"
                            color={!renewalAvailable ? "gray300" : undefined}
                            alignItems="center"
                            onClick={() => {
                                if (!renewalOptions.data?.options) return
                                setRenewalModalIsOpen(true)
                            }}
                        >
                            <Text variant="heading" level={3}>
                                {localize(section.menu.renewSubscription)}
                            </Text>
                            <Icon icon="arrowRight" />
                        </Flex>
                        <Divider horizontal spacing={0} />
                        <Flex
                            color={
                                order.data.cancelled || order.data.cancellationRequested
                                    ? "gray300"
                                    : "warning"
                            }
                            style={{
                                cursor:
                                    order.data.cancelled || order.data.cancellationRequested
                                        ? "inherit"
                                        : "pointer",
                            }}
                            justifyContent="space-between"
                            alignItems="center"
                            onClick={
                                order.data.cancelled || order.data.cancellationRequested
                                    ? undefined
                                    : () => setCancellationModalIsOpen(true)
                            }
                        >
                            <Text variant="heading" level={3}>
                                {localize(section.menu.cancelSubscription)}
                            </Text>
                            <Icon icon="arrowRight" />
                        </Flex>
                    </Flex>
                    <Flex
                        direction="column"
                        flex="566 0 0"
                        gap={24}
                        style={{ gridArea: "details", alignSelf: "stretch" }}
                    >
                        <Heading level={3}>{localize(section.details.heading)}</Heading>
                        {order.data.cancelled ? (
                            <Text variant="body" size="md">
                                <MarkdownView value={section.details.subscriptionIsCancelled} />
                            </Text>
                        ) : (
                            <OrderSummary order={order.data} {...section.details} />
                        )}
                        {orderId && (
                            <OrderReceipts order={order.data} {...section.details.receipts} />
                        )}
                    </Flex>
                    <CancelSubscription
                        {...section.cancelSubscription}
                        modalIsOpen={cancellationModalIsOpen}
                        setModalIsOpen={setCancellationModalIsOpen}
                        order={order.data}
                        refreshOrder={order.refresh}
                    />
                    <RenewSubscription
                        {...section.renewSubscription}
                        modalIsOpen={renewalModalIsOpen}
                        setModalIsOpen={setRenewalModalIsOpen}
                        order={order.data}
                        refreshOrder={order.refresh}
                    />
                    {tradeInForm.data && !!tradeInId && (
                        <TradeInModal
                            id={tradeInId}
                            form={tradeInForm.data}
                            onClose={() => {
                                setTradeInModalIsOpen(false)
                            }}
                            open={tradeInModalIsOpen}
                            productName={localize(model.name)}
                            months={order.data.monthsLeft}
                            originalPrice={order.data.rentalPeriod.monthlyPrice}
                            onTradeInConfirm={async (form, responses) => {
                                if (!order.data) {
                                    alert("Missing order")
                                    return
                                }

                                await postOrderTradeIn(order.data.id, {
                                    tradeIn: {
                                        form: form.id,
                                        version: form.version,
                                        responses,
                                    },
                                })
                                void order.refresh()
                                setTradeInModalIsOpen(false)
                            }}
                            {...section.tradeIn.modal}
                        />
                    )}
                </div>
            </Flex>
        </>
    )
}

function RenewSubscription({
    order,
    modalIsOpen,
    setModalIsOpen,
    refreshOrder,
    ...props
}: {
    order: GetPhoneOrderDto
    modalIsOpen: boolean
    setModalIsOpen: (isOpen: boolean) => void
    refreshOrder: () => Promise<void>
} & SubscriptionRenewalProps) {
    const localize = useLocalize()
    const [termsAccepted, setTermsAccepted] = useState(false)
    const [state, setState] = useState<"form" | "error">("form")
    const renewalOptions = useRenewalOptions(
        order.status === "delivered"
            ? (order.id as any as Uuid<"PhoneOrder"> | undefined) ?? null
            : null
    )
    // Selected option is referring to the number of months.
    const [selectedOptionMonths, setSelectedOptionMonths] = useState<number>()
    const selectedOption = useMemo(
        () => renewalOptions.data?.options.find((o) => o.months === selectedOptionMonths),
        [selectedOptionMonths, renewalOptions.data]
    )
    const terms = useGeneralPhoneOrderTermsAndConditions()

    useEffect(() => {
        if (!selectedOptionMonths && renewalOptions.data?.options) {
            setSelectedOptionMonths(renewalOptions.data.options.at(0)?.months)
        }
    }, [renewalOptions.data?.options, selectedOptionMonths])

    const renewalPeriodTotalPrice: CurrencyAmount | undefined = useMemo(() => {
        if (!selectedOption) return
        return {
            minorUnits: selectedOption.months * selectedOption.price.itemsTotal.minorUnits,
            majorUnits: selectedOption.months * selectedOption.price.itemsTotal.majorUnits,
            currency: selectedOption.price.itemsTotal.currency,
        }
    }, [selectedOption])

    const formatAmount = useFormatAmount()

    const handleRenewConfirmation = useCallback(async () => {
        try {
            if (!selectedOption || !terms.data) {
                throw new Error("Missing data.")
            }
            await postRenewal(
                order.id,
                selectedOption.months,
                terms.data.phoneOrder.id,
                terms.data.insurance.id
            )
            void refreshOrder()
            setModalIsOpen(false)
        } catch (err: any) {
            setState("error")
        }
    }, [order.id, refreshOrder, selectedOption, setModalIsOpen, terms.data])

    return (
        <Modal
            width={640}
            isOpen={modalIsOpen}
            header={{
                title: localize(props.title ?? {}),
                closeButton: true,
            }}
            onClose={() => setModalIsOpen(false)}
        >
            <Flex direction="column" gap={24}>
                {state === "error" ? (
                    <>
                        <Text variant="body" size="md">
                            <MarkdownView value={localize(props.renewalErrorText ?? {})} />
                        </Text>
                        <Button variant="dark" size="md" onClick={() => setState("form")}>
                            {localize({ no: "Prøv igjen", en: "Try again" })}
                        </Button>
                    </>
                ) : (
                    <>
                        {!!props.text && (
                            <Text variant="body" size="md">
                                {localize(props.text)}
                            </Text>
                        )}
                        {renewalOptions.data?.options.length === 0 && (
                            <Text variant="body" size="md">
                                {localize({
                                    no: "Ingen fornyelsesalternativer tilgjengelig.",
                                    en: "No renewal options available.",
                                })}
                            </Text>
                        )}
                        {renewalOptions.error && <div>{renewalOptions.error.detail}</div>}
                        {renewalOptions.data &&
                            selectedOption &&
                            selectedOptionMonths &&
                            renewalPeriodTotalPrice && (
                                <>
                                    <Flex
                                        borderRadius="lg"
                                        borderColor="gray200"
                                        padding="md"
                                        direction="column"
                                        css={css(
                                            { gap: 24 },
                                            responsiveCss("min", "md", { gap: 28 })
                                        )}
                                    >
                                        <div>
                                            <Heading level={4} margin={{ bottom: 16 }}>
                                                {localize(props.renewalOptionsLabel ?? {})}
                                            </Heading>
                                            <Flex
                                                direction="column"
                                                gap={8}
                                                css={css({ "> *": { flex: "1 0 0" } })}
                                            >
                                                <RadioGroup
                                                    value={selectedOptionMonths}
                                                    options={renewalOptions.data.options.map(
                                                        (rp) => ({
                                                            value: rp.months,
                                                            text: localize({
                                                                no: `${rp.months} måneder`,
                                                                en: `${rp.months} months`,
                                                            }),
                                                        })
                                                    )}
                                                    renderOption={(option, selected) => {
                                                        const rentalPeriodPrice =
                                                            renewalOptions.data?.options.find(
                                                                (o) => o.months === option.value
                                                            )?.price
                                                        if (!rentalPeriodPrice) return null
                                                        return (
                                                            <Button
                                                                variant="select"
                                                                selected={selected}
                                                                fullwidth
                                                                as="div"
                                                                size="md"
                                                            >
                                                                <Flex
                                                                    style={{
                                                                        width: "100%",
                                                                    }}
                                                                    justifyContent="space-between"
                                                                >
                                                                    <div
                                                                        style={
                                                                            !selected
                                                                                ? {
                                                                                      fontWeight: 300,
                                                                                  }
                                                                                : {}
                                                                        }
                                                                    >
                                                                        {option.value}{" "}
                                                                        {localize(monthsLong)}
                                                                    </div>
                                                                    <div>
                                                                        {formatAmount(
                                                                            rentalPeriodPrice.phone
                                                                        )}
                                                                        /{localize(monthsShort)}
                                                                    </div>
                                                                </Flex>
                                                            </Button>
                                                        )
                                                    }}
                                                    onChange={async (e, val) => {
                                                        setSelectedOptionMonths(val)
                                                    }}
                                                />
                                            </Flex>
                                        </div>
                                        <Divider horizontal spacing={0} />
                                        {!!selectedOption.price.insurance &&
                                            renewalOptions.data.insurance && (
                                                <>
                                                    <Flex direction="column">
                                                        <Flex gap={8}>
                                                            <Icon icon="shield" />
                                                            <Text variant="body" size="md">
                                                                {MustacheString(
                                                                    localize(
                                                                        props.renewInsuranceLabel ??
                                                                            {}
                                                                    ),
                                                                    {
                                                                        monthlyPrice: formatAmount(
                                                                            selectedOption.price
                                                                                .insurance
                                                                        ),
                                                                        monthsShort:
                                                                            localize(monthsShort),
                                                                        insuranceName: localize(
                                                                            renewalOptions.data
                                                                                .insurance
                                                                                .displayName
                                                                        ),
                                                                    }
                                                                )}
                                                            </Text>
                                                        </Flex>
                                                    </Flex>
                                                    <Divider horizontal spacing={0} />
                                                </>
                                            )}
                                        <Flex direction="column" gap={4}>
                                            <Flex
                                                alignItems="baseline"
                                                gap="4px 8px"
                                                style={{
                                                    whiteSpace: "nowrap",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <Text
                                                    variant="heading"
                                                    level={[2, ["min", "md", 3]]}
                                                    color="brand"
                                                >
                                                    {formatAmount(selectedOption.price.itemsTotal)}/
                                                    {localize(monthsShort)}
                                                </Text>
                                                <Text variant="body" size="md">
                                                    {MustacheString(
                                                        localize(props.totalPricePostFix ?? {}),
                                                        {
                                                            rentalPeriod: `${selectedOptionMonths}`,
                                                        }
                                                    )}
                                                </Text>
                                            </Flex>
                                            <Text variant="body" size="sm" color="gray300">
                                                {MustacheString(
                                                    localize(props.rentalPeriodTotalPrice ?? {}),
                                                    {
                                                        rentalPeriod: `${selectedOptionMonths}`,
                                                        totalPrice: `${formatAmount(renewalPeriodTotalPrice)}`,
                                                        monthsShort: localize(monthsShort),
                                                        monthsLong: localize(monthsLong),
                                                    }
                                                )}
                                            </Text>
                                        </Flex>
                                    </Flex>
                                    <Checkbox
                                        label={localize(props.termsAndConditionsLabel ?? {})}
                                        checked={termsAccepted}
                                        onChange={() => setTermsAccepted(!termsAccepted)}
                                    />
                                    <Button
                                        variant="primary"
                                        fullwidth
                                        disabled={!termsAccepted}
                                        onClick={handleRenewConfirmation}
                                    >
                                        {localize(props.confirmButtonText ?? {})}
                                    </Button>
                                </>
                            )}
                    </>
                )}
            </Flex>
        </Modal>
    )
}

function CancelSubscription({
    order,
    modalIsOpen,
    setModalIsOpen,
    refreshOrder,
    ...props
}: {
    order: GetPhoneOrderDto
    modalIsOpen: boolean
    setModalIsOpen: (isOpen: boolean) => void
    refreshOrder: () => Promise<void>
} & SubscriptionCancellationProps) {
    const localize = useLocalize()
    const cancellationTerms = useCancellationTerms(order.id)
    const formatAmount = useFormatAmount()
    const [state, setState] = useState<"prompt" | "success" | "error">("prompt")

    const handleCancellationConfirmation = useCallback(async () => {
        if (!cancellationTerms.data) {
            setState("error")
            return
        }

        try {
            await cancelSubscription(order.id, {
                cancellationFee: cancellationTerms.data.cancellationFee,
                cancellationTerms: cancellationTerms.data.cancellationTerms,
            })
            void refreshOrder()
            setState("success")
        } catch (err) {
            setState("error")
        }
    }, [cancellationTerms.data, order.id, refreshOrder])

    if (!cancellationTerms.data) return <></>

    return (
        <Modal
            isOpen={modalIsOpen}
            onClose={() => setModalIsOpen(false)}
            header={{ title: localize(props.title), closeButton: true }}
        >
            <Flex direction="column" gap={24}>
                {state === "prompt" ? (
                    <>
                        <Text variant="body" size="md">
                            {MustacheString(localize(props.cancellationPrompt), {
                                cancellationFee: formatAmount(
                                    cancellationTerms.data.cancellationFee
                                ),
                                monthsRemaining: cancellationTerms.data.monthsRemaining.toString(),
                            })}
                        </Text>
                        <Text variant="body" size="md">
                            <MarkdownView value={cancellationTerms.data.cancellationTerms} />
                        </Text>
                        <Flex
                            gap={8}
                            css={css(
                                { flexWrap: "wrap" },
                                responsiveCss("max", "xs", {
                                    flexDirection: "column",
                                    alignItems: "stretch",
                                })
                            )}
                        >
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setModalIsOpen(false)}
                            >
                                {localize(props.cancelButtonText)}
                            </Button>
                            <Button
                                variant="dark"
                                size="sm"
                                onClick={handleCancellationConfirmation}
                            >
                                {localize(props.confirmButtonText)}
                            </Button>
                        </Flex>
                    </>
                ) : state === "error" ? (
                    <>
                        <Text variant="body" size="md">
                            <MarkdownView value={props.cancellationErrorText} />
                        </Text>
                        <Button variant="secondary" size="sm" onClick={() => setModalIsOpen(false)}>
                            {localize(props.closeButtonText)}
                        </Button>
                    </>
                ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                state === "success" ? (
                    <>
                        <Text variant="body" size="md">
                            <MarkdownView value={props.cancellationSuccessText} />
                        </Text>
                        <Button variant="secondary" size="sm" onClick={() => setModalIsOpen(false)}>
                            {localize(props.closeButtonText)}
                        </Button>
                    </>
                ) : null}
            </Flex>
        </Modal>
    )
}

function OrderSummary({ order, ...props }: { order: GetPhoneOrderDto } & SubscriptionDetailsProps) {
    const price = order.rentalPeriod.monthlyPrice
    const localize = useLocalize()
    const formatAmount = useFormatAmount()
    const insuranceOptions = useInsuranceOptions(
        order.model.id,
        order.phone.storage,
        order.phone.color,
        order.rentalPeriod.months
    )
    const [showInsuranceTerms, setShowInsuranceTerms] = useState(false)
    const insurance = insuranceOptions.data?.find((io) => io.id === order.insurance)

    const statusToDisplay: Localized<string> | undefined = useMemo(() => {
        if (order.customStatus) return order.customStatus
        if (order.cancellationRequested && props.status.cancellationRequestedStatusLabel)
            return props.status.cancellationRequestedStatusLabel
        if (order.status && order.status in props.status.statusLabels) {
            return props.status.statusLabels[order.status]
        }
    }, [
        order.cancellationRequested,
        order.customStatus,
        order.status,
        props.status.cancellationRequestedStatusLabel,
        props.status.statusLabels,
    ])

    dayjs.extend(advancedFormat)

    return (
        <Flex direction="column" gap={24}>
            {statusToDisplay ? (
                <CheckoutStepSummaryBlock
                    step={localize(props.status.heading)}
                    icon={props.status.icon}
                    title={localize(statusToDisplay)}
                />
            ) : null}
            <CheckoutStepSummaryBlock
                step={MustacheString(localize(props.subscriptionPeriod.heading), {
                    nextChargeDate: dayjs(order.nextChargeDate).format("DD.MM.YYYY"),
                })}
                icon="calendar"
                title={
                    props.subscriptionPeriod.subtitle
                        ? MustacheString(localize(props.subscriptionPeriod.subtitle), {
                              amount: formatAmount(order.nextCharge.total),
                          })
                        : undefined
                }
                text={MustacheString(localize(props.subscriptionPeriod.text), {
                    monthsLeft: order.monthsLeft.toString(),
                    rentalPeriodMonths: order.rentalPeriod.months.toString(),
                })}
                subtitle={order.nextCharge.discounts?.map((d) => (
                    <Text variant="body" size="md" color="brand">
                        {MustacheString(localize(props.subscriptionPeriod.discountText), {
                            discountName: localize(d.name),
                            discountAmount: formatAmount(d.amount),
                            remainingMonths: d.remainingMonths.toString(),
                            totalMonths: d.totalMonths.toString(),
                        })}
                    </Text>
                ))}
                label={
                    props.subscriptionPeriod.label
                        ? localize(props.subscriptionPeriod.label)
                        : undefined
                }
            />
            <PaymentSummary
                originalPrice={price.phone.majorUnits}
                discountedPrice={
                    order.tradeIn && order.tradeIn.discount.remainingMonths > 0
                        ? price.phone.majorUnits - order.tradeIn.discount.amount.majorUnits
                        : price.phone.majorUnits
                }
                currency={localize(
                    localizedCurrencyCodes[price.itemsTotal.currency] ?? {
                        no: price.itemsTotal.currency,
                    }
                )}
                label={localize(props.payment.monthlyLabel)}
                itemName={localize(order.model.name)}
                rentalPeriodUnit={localize(monthsShort)}
                text={
                    order.tradeIn && order.tradeIn.discount.remainingMonths > 0
                        ? localize(props.payment.phonePriceWithTradeInText)
                        : localize(props.payment.phonePriceText)
                }
            />
            {insurance ? (
                <>
                    <CheckoutStepSummaryBlock
                        step={localize(props.insurance.heading)}
                        icon="shield"
                        title={localize(insurance.displayName)}
                        price={price.insurance ? formatAmount(price.insurance) : undefined}
                        list={insurance.sellingPoints.map((sp) => ({
                            id: sp.id.valueOf(),
                            icon: sp.icon,
                            text: localize(sp.text),
                        }))}
                        text={
                            <Text
                                variant="body"
                                size="md"
                                onClick={() => setShowInsuranceTerms(true)}
                                underline
                                style={{ cursor: "pointer" }}
                            >
                                {localize(props.insurance.showTermsLink)}
                            </Text>
                        }
                        label={props.insurance.label ? localize(props.insurance.label) : undefined}
                    />
                    <Modal
                        isOpen={showInsuranceTerms}
                        header={{
                            title: MustacheString(localize(props.insurance.termsModalTitle), {
                                insuranceName: localize(insurance.displayName),
                            }),
                            closeButton: true,
                        }}
                        onClose={() => setShowInsuranceTerms(false)}
                    >
                        <Text variant="body" size="sm">
                            <MarkdownView value={localize(insurance.termsAndConditions.content)} />
                        </Text>
                    </Modal>
                </>
            ) : null}
        </Flex>
    )
}

function OrderReceipts({
    order,
    ...props
}: { order: GetPhoneOrderDto } & SubscriptionDetailsProps["receipts"]) {
    const localize = useLocalize()
    const formatAmount = useFormatAmount()

    dayjs.extend(advancedFormat)

    return (
        <Flex borderRadius="md" padding="md" borderColor="gray200" direction="column" gap={16}>
            <Flex justifyContent="space-between" alignItems="center">
                <Flex gap={8}>
                    <Icon icon="receipt" /> <Heading level={4}>{localize(props.heading)}</Heading>
                </Flex>
            </Flex>
            <Flex gap={24} direction="column">
                {order.receipts.length ? (
                    order.receipts.map((receipt, index) => (
                        <Fragment key={receipt.id.valueOf()}>
                            {index > 0 && <Divider horizontal spacing={0} />}
                            <a
                                href={`/api/phone-orders/${order.id}/pdf-receipts/${receipt.id}`}
                                style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    flexDirection: "row",
                                    width: "100%",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Text variant="body" size="md">
                                    {dayjs(receipt.date.valueOf()).format(
                                        localize(props.dateFormat)
                                    )}
                                </Text>
                                <Flex gap={24}>
                                    <Text variant="body" size="md">
                                        {formatAmount(receipt.total)}
                                    </Text>
                                    <Text variant="body" size="md" underline>
                                        {localize(props.downloadLinkText)}
                                    </Text>
                                </Flex>
                            </a>
                        </Fragment>
                    ))
                ) : (
                    <Text variant="body" size="sm" color="gray300">
                        {localize(props.noReceiptsMessage)}
                    </Text>
                )}
            </Flex>
        </Flex>
    )
}
